import { WebComponentProps } from '../types';

/**
 * Load a file (CSS or JS) from a given URL
 * @param url
 * @param type
 * @param id
 */
export function loadFile(url: string, type: 'CSS' | 'JS:MODULE' | 'JS', id?: string): Promise<string> {
  if (typeof id === 'string') {
    document.getElementById(id)?.remove();
  }

  return new Promise((resolve, reject) => {
    const isJs = ['JS', 'JS:MODULE'].includes(type);
    const element: HTMLScriptElement | HTMLLinkElement = isJs ? document.createElement('script') : document.createElement('link');

    if (typeof id === 'string') {
      element.id = id;
    }

    if (type === 'CSS') {
      (element as HTMLLinkElement).rel = 'stylesheet';
      (element as HTMLLinkElement).href = url;
    } else {
      (element as HTMLScriptElement).src = url;
      if (type === 'JS:MODULE') {
        (element as HTMLScriptElement).type = 'module';
      }
      (element as HTMLScriptElement).async = true;
    }

    element.onload = () => resolve(`JS loaded: ${url}`);
    element.onerror = () => reject(new Error(`Failed to load JS: ${url}`));

    if (type === 'CSS') {
      document.head.appendChild(element);
    } else if (isJs) {
      document.body.appendChild(element);
    }
  });
}

/**
 * Create a custom web component with a given tag and attributes
 * @param tag
 * @param customProps
 * @param destination
 */
export function createCustomWebComponent(tag: string, customProps: WebComponentProps, destination: HTMLElement | null = null): void {
  const customComponent = document.createElement(tag);
  customComponent.setAttribute('custom-props', JSON.stringify(customProps));

  console.debug('Create custom component ', tag, ' with props:', customProps);

  // Check and remove already web component loaded
  const elements = document.querySelectorAll(tag);
  if (elements.length > 0) {
    console.debug('Remove already loaded component:', elements);
    elements.forEach(element => element.remove());
  }

  // Si se le provee un destino, se añade el componente a ese destino, de lo contrario se añade al body
  if (destination !== null) {
    destination.appendChild(customComponent);
  } else {
    document.body.prepend(customComponent);
  }
}

/**
 * Validate if an object has all the keys provided
 * @param keys
 * @param obj
 */
export function validateKeysString<T>(keys: (keyof T)[], obj: Partial<T>): { isValid: boolean; missingKeys: (keyof T)[]; invalidTypeKeys: (keyof T)[] } {
  if (!Array.isArray(keys)) {
    throw new Error('The first argument must be an array of strings.');
  }

  if (typeof obj !== 'object' || obj === null) {
    throw new Error('The second argument must be a valid object.');
  }

  const missingKeys = keys.filter(key => !(key in obj));
  const invalidTypeKeys = keys.filter(key => typeof obj[key] !== 'string' && missingKeys.indexOf(key) === -1);

  return {
    isValid: missingKeys.length === 0 && invalidTypeKeys.length === 0,
    missingKeys,
    invalidTypeKeys,
  };
}

/**
 * Sanitiza el locale
 */
export function sanitizeLocale(locale: string): string {
  return locale.replace('-', '_');
}
